import { trigger, transition, style, animate } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from '@shared/auth/auth.service';
import { SharedService } from '@shared/services/shared.service';
import { IAddress } from 'app/models/address/address';
import { IEntityOwner } from 'app/models/licenses/entity-owner';
import { IInHomeCareChange } from 'app/models/licenses/in-home-care/in-home-care-change';
import { IStaff } from 'app/models/user/staff';
import { InHomeCareChangeService } from 'app/services/license/in-home-care/in-home-care-change.service';

@Component({
  selector: 'in-home-care-change',
  templateUrl: './manage-in-home-care-change.component.html',
  styleUrls: ['./manage-in-home-care-change.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})
export class ManageInHomeCareChangeComponent implements OnInit {
  @Input() licenseId: number = 0;
  @Input() assigned: boolean = false;
  @Output() changeApproved = new EventEmitter<string>();

  public ihc: string = "IHC";
  public includeApproved: boolean = false;
  public includeDenied: boolean = false;
  public effectiveDate: Date | null = null;

  public changes: IInHomeCareChange[] = [];
  public change: IInHomeCareChange = {
    newParent: '',
    subunits: [],
    id: 0,
    licenseId: 0,
    licenseGuid: '',
    entityName: '',
    agencyDBAName: '',
    entity: '',
    type: '',
    city: '',
    originalValue: '',
    newValue: '',
    dateRequested: '',
    requestedBy: '',
    effectiveDateOfChange: '',
    signature: '',
    title: '',
    dateSigned: '',
    requiresPayment: false,
    assignedToId: '',
    assignedToName: '',
    isRenewal: false,
    approved: false,
    denied: false,
    determinationBy: '',
    dateOfDetermination: ''
  }

  public owner:IEntityOwner = {
    id: 0,
    parentId: 0,
    firstName: '',
    lastName: '',
    phone: '',
    fax: '',
    taxId: '',
    contactWithClient: false,
    backgroundCheckDate: '',
    backgroundCheckCompleted: false,
    archived: false
  }

  public address: IAddress = {
    id: 0,
    state: '',
    city: '',
    street: '',
    zip: '',
    county: '',
    archived: false,
    parentId: 0,
    addressTypes: []
  }

  public staff: IStaff[] = [];
  public skus: { [key: string]: string } = {
    "Owner": "IHC11",
    "LimitedBasic": "IHC12",
    "LimitedIntermediate": "IHC13",
    "LimitedComprehensive": "IHC14",
    "BasicIntermediate": "IHC15",
    "BasicComprehensive": "IHC16",
    "IntermediateComprehensive": "IHC17",
  };

  public changeColumns: string [] = ['licenseGuid', 'entityName', 'agencyDBAName', 'city', 'type', 'approved', 'effectiveDate', 'assignedToName'];
  public changeSource = new MatTableDataSource<IInHomeCareChange>(this.changes);
  @ViewChild('changePaginator', {static: false}) changePaginator!: MatPaginator;
  @ViewChild('changeSort', {static: false}) changeSort!: MatSort;

  public sharedService = inject(SharedService);
  public authService = inject(AuthService);
  public changeService = inject(InHomeCareChangeService);

  ngOnInit(): void {
    if(this.licenseId > 0){
      this.getChangesByLicenseId();
    }
    else{
      if(this.assigned){
        this.getAssignedChanges();
      }
      else{
        this.getChanges()
      }
    }
    this.getStaff();
  }

  getChanges(): void {
    this.changeService.getAllChanges(this.includeApproved, this.includeDenied).subscribe({
      next: response => this.changes = response,
      error: e => console.log('error getting changes', e),
      complete: () => {
        this.updateChangeTable();
      }
    });
  }

  getAssignedChanges(): void {
    this.changeService.getAssignedChanges().subscribe({
      next: response => this.changes = response,
      error: e => console.log('error getting assigned changes', e),
      complete: () => {
        this.updateChangeTable();
      }
    });
  }

  getChangesByLicenseId(): void {
    this.changeService.getChangesByLicenseId(this.licenseId).subscribe({
      next: response => this.changes = response,
      error: e => console.log('error getting changes by license id', e),
      complete: () => {
        this.updateChangeTable();
      }
    });
  }

  getStaff(): void {
    this.changeService.getStaff(this.ihc).subscribe({
      next: response => {
        this.staff = response;
      },
      error: error => {
        console.log('error getting staff', error);
        this.sharedService.toastr.error('An error occurred while getting staff.');
      }
    });
  }

  updateChangeTable(): void {
    setTimeout(() => {
      this.changeSource.data = this.changes;
      this.changeSource.sort = this.changeSort;
      this.changeSource.paginator = this.changePaginator;
    }, 500);
  }

  filterChanges(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.changeSource.filter = filterValue.trim().toLowerCase();
  }

  close(): void {
    this.change = this.getEmptyChange();
    this.updateChangeTable();
  }

  updateEffectiveDate(): void {
    this.sharedService.openConfirm('Are you sure you want to update the effective date?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.change.effectiveDateOfChange = this.effectiveDate ? this.effectiveDate.toDateString() : '';
          this.changeService.updateChange(this.change).subscribe({
            complete: () => {
              this.sharedService.toastr.success('Effective Date Updated');
              let changeIndex = this.changes.findIndex(c => c.id === this.change.id);
              this.changes[changeIndex].effectiveDateOfChange = this.change.effectiveDateOfChange;
              this.updateChangeTable();
            },
            error: e => {
              this.sharedService.toastr.error('Could Not Update Effective Date');
              console.log('error updating effective date', e);
            }
          });
        }
      }
    );
  }

  getEmptyChange(): IInHomeCareChange {
    return {
      newParent: '',
      subunits: [],
      id: 0,
      licenseId: 0,
      licenseGuid: '',
      entityName: '',
      agencyDBAName: '',
      entity: '',
      type: '',
      city: '',
      originalValue: '',
      newValue: '',
      dateRequested: '',
      requestedBy: '',
      effectiveDateOfChange: '',
      signature: '',
      title: '',
      dateSigned: '',
      requiresPayment: false,
      assignedToId: '',
      assignedToName: '',
      isRenewal: false,
      approved: false,
      denied: false,
      determinationBy: '',
      dateOfDetermination: ''
    }
  }

  selectChange(change: IInHomeCareChange): void {
    if(change.entity === this.sharedService.entityOwner && change.type === this.sharedService.delete){
      this.owner = JSON.parse(change.originalValue);
    }
    if((change.entity === this.sharedService.branchOperation || change.entity === this.sharedService.address) &&
        change.type === this.sharedService.change){
      try{
        this.address = JSON.parse(change.originalValue);
      } catch (error) {
        this.address = {
          id: 0,
          state: '',
          city: '',
          street: '',
          zip: '',
          county: '',
          archived: false,
          parentId: 0,
          addressTypes: []
        };
      }
    }
    this.effectiveDate = change.effectiveDateOfChange ? new Date(change.effectiveDateOfChange) : null;
    this.change = change;
  }

  public get canApprove(): boolean {
    if(this.authService.isSurveyor ||
       this.authService.isOPA3 ||
       this.authService.isProgramManager ||
       this.authService.isSectionManager ||
       this.authService.isSectionAdministrator)
    {
      return true;
    }

    const adminEntities = new Set([
      this.sharedService.address,
      this.sharedService.nurse
    ]);

    const adminOrSurveyorOrOPA3Entities = new Set([
      this.sharedService.entityOwner,
      this.sharedService.branchOperation,
      this.sharedService.administratorApplication
    ]);

    const surveyorEntities = new Set([
      this.sharedService.classification,
      this.sharedService.parent
    ]);

    if (adminEntities.has(this.change.entity)) {
      return this.authService.isAdmin || this.authService.isLicenseSpecialist || this.authService.isSurveyor;
    }

    if (adminOrSurveyorOrOPA3Entities.has(this.change.entity)) {
      return this.authService.isAdmin || this.authService.isSurveyor;
    }

    if (surveyorEntities.has(this.change.entity)) {
      return this.authService.isSurveyor || (this.change.entity === this.sharedService.parent && this.authService.isOPA3);
    }

    return false;
  }

  approveChange(approved: boolean): void {
    if(this.change.isRenewal && approved && this.change.entity === this.sharedService.entityOwner &&
       this.change.type !== this.sharedService.change){
      this.sharedService.openNotice('You must approve the renewal application before approving this owner change.');
      return;
    }

    this.sharedService.openConfirm('Are you sure you want to ' + (approved ? 'Approve' : 'Deny') + ' this change?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.changeService.approveChange(this.change.id, approved).subscribe({
            complete: () => {
              this.sharedService.toastr.success('Change ' + (approved ? 'Approved' : 'Denied'));
              if(this.licenseId > 0){
                this.changeApproved.emit(this.change.entity);
              }
              let changeIndex = this.changes.findIndex(c => c.id === this.change.id);
              this.changes[changeIndex].approved = approved;
              this.changes[changeIndex].denied = !approved;
              this.change = this.getEmptyChange();
              this.updateChangeTable();
            },
            error: e => {
              this.sharedService.toastr.error('Could Not Approve Change');
              console.log('error approving change', e);
            }
          });
        }
      }
    );
  }

  assignChange(event: MatSelectChange, changeId: number): void {
    let user: IStaff = this.staff.find(staff => staff.name === event.value);
    if(user === undefined){
      let emptyUser: IStaff = {
        id: '',
        name: '',
        email: ''
      }
      user = emptyUser;
    }
    this.changeService.assignChange(changeId, user).subscribe({
      complete: () => {
        this.changes.find(c => c.id == changeId).assignedToName = user.name;
        if(user === null || user.name === null || user.name.length === 0) {
          this.sharedService.toastr.success("Change unassigned");
        }
        else {
          this.sharedService.toastr.success("Change assigned to " + user.name);
        }
      },
      error: error => {
        console.log('error assigning change', error);
        this.sharedService.toastr.error('An error occurred while assigning the change.');
      }
    });
  }
}
